import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Checkbox,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import {
  Casino as CasinoIcon,
  SelectAll as SelectAllIcon,
  CheckBox as CheckBoxIcon,
} from '@mui/icons-material';
import exerciseContainerService from '../services/exerciseContainerService';
import { CompositePatternItem } from '../types';

export interface CompositeConfirmOptions {
  selectedPatternIds: number[];
  countsPerPattern: Record<number, number>;
  containerId: number | null;
  compositeConfig?: {
    composite_id: number;
    pool: number[];
    count: number;
    group_id: string;
  };
}

interface CompositePatternInstanceModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedPatternIds: number[], countsPerPattern: Record<number, number>, containerId: number | null, compositeConfig?: CompositeConfirmOptions['compositeConfig']) => void;
  compositeId: number;
  compositeTitle: string;
  items: CompositePatternItem[];
}

const CompositePatternInstanceModal: React.FC<CompositePatternInstanceModalProps> = ({
  open,
  onClose,
  onConfirm,
  compositeId,
  compositeTitle,
  items,
}) => {
  const [tab, setTab] = useState(0); // 0=all, 1=random, 2=select
  const [randomCount, setRandomCount] = useState<number | null>(null);
  const [randomPool, setRandomPool] = useState<Set<number>>(new Set());
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [selectedItemCounts, setSelectedItemCounts] = useState<Record<number, number>>({});
  const [defaultContainerId, setDefaultContainerId] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setTab(0);
      setRandomCount(null);
      setRandomPool(new Set(items.map(item => item.exercise_pattern)));
      setSelectedItems(new Set());
      setSelectedItemCounts({});
      loadDefaultContainer();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDefaultContainer = async () => {
    try {
      const allContainers = await loadAllContainersRecursively();
      const defaultContainer = allContainers.find(
        (c: any) => c.title === 'Exercices générés à la volée pour peupler une séance'
      );
      setDefaultContainerId(defaultContainer?.id || null);
    } catch (error) {
      console.error('Error loading containers:', error);
    }
  };

  const loadAllContainersRecursively = async (): Promise<any[]> => {
    const allContainers: any[] = [];
    const visited = new Set<number>();
    const loadLevel = async (parentId: number | null) => {
      const containers = await exerciseContainerService.getContainers(parentId);
      for (const container of containers) {
        if (!visited.has(container.id)) {
          visited.add(container.id);
          allContainers.push(container);
          await loadLevel(container.id);
        }
      }
    };
    await loadLevel(null);
    return allContainers;
  };

  const toggleItem = (patternId: number) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(patternId)) {
        next.delete(patternId);
        setSelectedItemCounts(prev => {
          const copy = { ...prev };
          delete copy[patternId];
          return copy;
        });
      } else {
        next.add(patternId);
        if (!selectedItemCounts[patternId]) {
          setSelectedItemCounts(prev => ({ ...prev, [patternId]: 1 }));
        }
      }
      return next;
    });
  };

  const setItemCount = (patternId: number, count: number) => {
    setSelectedItemCounts(prev => ({ ...prev, [patternId]: count }));
    setSelectedItems(prev => {
      const next = new Set(prev);
      next.add(patternId);
      return next;
    });
  };

  const handleConfirm = () => {
    if (tab === 0) {
      // All: one exercise per child pattern
      const allIds = items.map(item => item.exercise_pattern);
      const counts: Record<number, number> = {};
      allIds.forEach(id => { counts[id] = 1; });
      onConfirm(allIds, counts, defaultContainerId);
    } else if (tab === 1) {
      // Random: pick randomCount patterns at random (with replacement) from pool
      if (!randomCount || randomPool.size === 0) return;
      const poolItems = items.filter(item => randomPool.has(item.exercise_pattern));
      const counts: Record<number, number> = {};
      const ids: number[] = [];
      for (let i = 0; i < randomCount; i++) {
        const pick = poolItems[Math.floor(Math.random() * poolItems.length)];
        const pid = pick.exercise_pattern;
        if (!counts[pid]) {
          ids.push(pid);
          counts[pid] = 1;
        } else {
          counts[pid]++;
        }
      }
      const groupId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
      const compositeConfig = {
        composite_id: compositeId,
        pool: Array.from(randomPool),
        count: randomCount,
        group_id: groupId,
      };
      onConfirm(ids, counts, defaultContainerId, compositeConfig);
    } else {
      // Select: specific children with counts
      if (selectedItems.size === 0) return;
      const ids = Array.from(selectedItems);
      onConfirm(ids, selectedItemCounts, defaultContainerId);
    }
    onClose();
  };

  const isConfirmDisabled = () => {
    if (tab === 0) return items.length === 0;
    if (tab === 1) return !randomCount || randomPool.size === 0;
    return selectedItems.size === 0;
  };

  const totalSelectedExercises = () => {
    if (tab === 0) return items.length;
    if (tab === 1) return randomCount || 0;
    return Array.from(selectedItems).reduce((sum, id) => sum + (selectedItemCounts[id] || 1), 0);
  };

  const numberButtons = Array.from({ length: 10 }, (_, i) => i + 1);
  const countButtons = [1, 2, 3, 4, 5];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Pattern composé</Typography>
        <Typography variant="body2" color="text.secondary">
          {compositeTitle} ({items.length} patterns)
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<SelectAllIcon />} label="Tous" iconPosition="start" />
          <Tab icon={<CasinoIcon />} label="Au hasard" iconPosition="start" />
          <Tab icon={<CheckBoxIcon />} label="Sélection" iconPosition="start" />
        </Tabs>

        {/* Tab 0: All */}
        {tab === 0 && (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Ajouter <strong>1 exercice</strong> pour chacun des {items.length} patterns :
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {items.map((item) => (
                <Chip key={item.id} label={item.pattern_title} color="primary" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        {/* Tab 1: Random */}
        {tab === 1 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              Combien d'exercices tirer au hasard ?
            </Typography>
            <Grid container spacing={1} sx={{ mt: 1 }}>
              {numberButtons.map((num) => (
                <Grid item xs={2.4} key={num}>
                  <Button
                    variant={randomCount === num ? 'contained' : 'outlined'}
                    color="primary"
                    fullWidth
                    sx={{ height: 64, fontSize: '1.8rem', fontWeight: 'bold' }}
                    onClick={() => setRandomCount(num)}
                  >
                    {num}
                  </Button>
                </Grid>
              ))}
            </Grid>

            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }} fontWeight={600}>
              Parmi les patterns suivants :
            </Typography>
            <Grid container spacing={1}>
              {items.map((item) => {
                const included = randomPool.has(item.exercise_pattern);
                return (
                  <Grid item xs={6} key={item.id}>
                    <Box
                      onClick={() => {
                        setRandomPool(prev => {
                          const next = new Set(prev);
                          if (next.has(item.exercise_pattern)) {
                            next.delete(item.exercise_pattern);
                          } else {
                            next.add(item.exercise_pattern);
                          }
                          return next;
                        });
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        py: 0.75,
                        px: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: included ? 'primary.main' : 'divider',
                        backgroundColor: included ? 'rgba(33, 150, 243, 0.08)' : 'transparent',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: included ? 'rgba(33, 150, 243, 0.15)' : 'action.hover' },
                      }}
                    >
                      <Checkbox checked={included} size="small" sx={{ p: 0, mr: 1 }} />
                      <Typography variant="body1">
                        {item.pattern_title}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {randomCount && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {randomCount} exercice{randomCount > 1 ? 's' : ''} tiré{randomCount > 1 ? 's' : ''} au
                hasard parmi {randomPool.size} pattern{randomPool.size > 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
        )}

        {/* Tab 2: Select specific */}
        {tab === 2 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              Sélectionnez les patterns et le nombre d'exercices pour chacun
            </Typography>
            {items.map((item) => {
              const isSelected = selectedItems.has(item.exercise_pattern);
              const count = selectedItemCounts[item.exercise_pattern] || 1;
              return (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 1,
                    px: 1,
                    mb: 1,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    backgroundColor: isSelected ? 'rgba(33, 150, 243, 0.04)' : 'transparent',
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={() => toggleItem(item.exercise_pattern)}
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="body2" sx={{ flex: 1, fontWeight: isSelected ? 600 : 400 }}>
                    {item.pattern_title}
                  </Typography>
                  {isSelected && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {countButtons.map((n) => (
                        <Button
                          key={n}
                          variant={count === n ? 'contained' : 'outlined'}
                          size="small"
                          sx={{
                            minWidth: 40,
                            height: 40,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                          }}
                          onClick={() => setItemCount(item.exercise_pattern, n)}
                        >
                          {n}
                        </Button>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={isConfirmDisabled()}
        >
          Créer {totalSelectedExercises()} exercice{totalSelectedExercises() > 1 ? 's' : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompositePatternInstanceModal;
